import * as angular from 'angular';
import * as _ from 'lodash';

import template from './countv2.tpl.html';
import {
	Counts, LineCounts, LineEquipmentGroup, LineEquipmentItem, LineItem,
	LineJunctionItem
} from "../../models/counts/lineCounts";
import { Plant } from "../../models/navigation";
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import { apsEnums } from "../../shared/dto/aps.enums";
import { States } from "../../shared/config/constant.module";
import { apsDisplayEnums } from "../../shared/dto/enums";
import CountsServiceName, { CountHub, CountsService } from "../../services/counts/counts.service";
import UIUtilityServiceName, { EnumOption, UIUtilityService } from "../../services/utility/uiUtility.service";
import LineConfigsServiceName, { LineConfigsService } from "../../services/lineConfigs/lineConfigs.service";
import LoaderServiceName, { LoaderService } from "../../components/loader/loader.service";
import NavigationServiceName, { NavigationService } from "../../services/navigation/navigation.service";
import AuthDataServiceName, { AuthDataService } from "../../services/auth/authData.service";
import GroupRejectsServiceName, { GroupRejectsService } from "../../services/counts/groupRejects.service";
import EquipmentRejectsName, { openEquipmentRejects } from "./equipmentRejects.controller";
import { IBtn, IBtnArrayOptions } from '../dashboard/btnarray.interfaces';
import { StateParams } from '@uirouter/angularjs';

let requires = [CountsServiceName, UIUtilityServiceName, LineConfigsServiceName, LoaderServiceName,
	NavigationServiceName, AuthDataServiceName, GroupRejectsServiceName, EquipmentRejectsName];

export interface ICountControllerv2 {
	$stateParams: StateParams;
	model: LineCounts;
	areas: CountArea[];
	plant: Plant;
	shop: apsViewModels.ShopNavigation;
	hub: CountHub;
	countDisplayIntervalOptions: EnumOption[];
	countsInterval: apsDisplayEnums.CountDisplayInterval;
	canDisableLine: boolean;
	viewRejectDetail(line: LineItem, equipment: LineEquipmentItem): void;
	viewGroupRejectDetail(equipmentGroup: LineEquipmentGroup): void;
	countsIntervalChanged(): void;
	calculateSpeed(gobsCount: Counts): number;
	toggleLineActive(line: LineItem): void;
	wareLossClicked(): void;
	showHelp(helpScreen: string): void;
	toggleHelpWindow(): void;
	showHelpWindow: boolean;
	helpText: string;
	cavities: apsViewModels.CavityCountSummary[];
	hasCavities: boolean;
	userId: string;
	navigationUrls: IBtnArrayOptions;
}

export interface CountRow {
	isLine: boolean;
	line: LineItem;
	junction: LineJunctionItem;
	cssClass: string;
}

export interface CountArea {
	rows: CountRow[];
}

export default angular.module('aps.controller.countsv2', requires)
	.config(countsConfig)
	.controller('CountsCtrlv2', countsCtrlv2)
	.name;

function countsConfig($stateProvider: angular.ui.IStateProvider) {
	$stateProvider.state({
		name: States.COUNTS_HORIZONTAL_V2,
		url: '/countsv2',
		controller: 'CountsCtrlv2 as vm',
		template: template,
		data: { pageTitle: 'Counts' }
	});
}

function countsCtrlv2(
	$stateParams: StateParams,
	countsService: CountsService,
	lineConfigsService: LineConfigsService,
	loaderService: LoaderService,
	navigationService: NavigationService,
	authDataService: AuthDataService,
	groupRejectsService: GroupRejectsService,
	uiUtilityService: UIUtilityService,
	$scope: angular.IScope,
	$timeout: angular.ITimeoutService,
	$modal: mm.foundation.modal,
	$state: angular.ui.IStateService,
	$interval: angular.IIntervalService): ICountControllerv2 {

	let vm: ICountControllerv2 = {
		$stateParams: $stateParams,
		model: null,
		areas: [],
		plant: null,
		shop: null,
		hub: null,
		canDisableLine: false,
		countDisplayIntervalOptions: uiUtilityService.getListOptionsFromEnum(
			apsDisplayEnums.CountDisplayInterval,
			null, null, o => o),
		countsInterval: authDataService.getCountInterval(),
		viewRejectDetail: null,
		viewGroupRejectDetail: null,
		calculateSpeed: null,
		countsIntervalChanged: null,
		toggleLineActive: null,
		wareLossClicked: null,
		showHelp: null,
		showHelpWindow: false,
		helpText: null,
		toggleHelpWindow: null,
		cavities: [],
		hasCavities: false,
		userId: null,
		navigationUrls: {
			buttons: [
				{ 'name': 'Horizontal Layout', 'value': 'horizontal', 'selected': false },
				{ 'name': 'Vertical Layout', 'value': 'vertical', 'selected': false },
				{ 'name': 'Vertical Layout (v2)', 'value': 'vc2', 'selected': false },
			],
			select: (button: IBtn) => {
				let stateParams = {
					plantId: vm.$stateParams.plantId,
					shopId: vm.$stateParams.shopId,
					userId: vm.$stateParams.userId,
				};

				switch (button.value) {
					case "horizontal": $state.go(States.COUNTS_HORIZONTAL, stateParams); break;
					case "horizontalv2": $state.go(States.COUNTS_HORIZONTAL_V2, stateParams); break;
					case "vertical": $state.go(States.COUNTS_VERTICAL, stateParams); break;
					case "vc2": $state.go(States.COUNTS_VERTICAL_V2, stateParams); break;
				}
			}
		}
	};

	vm.userId = $stateParams.userId;
	let shopId = $stateParams.shopId;

	vm.canDisableLine = authDataService.isPlantOrAPSIOAdminForCurrentPlant();

	let plantShopsPromise = navigationService.getNavigation().then(navModel => {
		vm.plant = navModel.selectedPlant;
		vm.shop = navModel.selectedShop;
	});

	let countsPromise = plantShopsPromise.then(() =>
		countsService.getLineCounts(shopId, vm.plant, vm.countsInterval, vm.canDisableLine)
			.then(result => {
				vm.model = result;
				vm.areas = mapModelToAreas(vm.model);

				let rejects = vm.model.shopConfig.jobRuns[0].machineRejectPercentages;
				if (!!rejects) { 
					vm.areas.forEach(area => {
						area.rows.forEach(row => {
							if (!row.isLine) { return; }
							row.line.equipment.forEach(equipment => {
								let equipWithRejectPercentage = rejects.filter(item => item.selectedMachine === equipment.name );
								if (equipWithRejectPercentage.length > 0) {
									equipment.rejectMinPercentage = equipWithRejectPercentage[0].rejectedMinPercentage;
									equipment.rejectMaxPercentage = equipWithRejectPercentage[0].rejectedMaxPercentage;
								}
							});
						});
					});
				}

				for (let id in vm.model.equipment) {
					if (vm.model.equipment[id].type === apsEnums.LineEquipmentType.HEYE
						|| vm.model.equipment[id].type === apsEnums.LineEquipmentType.CheckDetector
						|| vm.model.equipment[id].type === apsEnums.LineEquipmentType.MX4
						|| vm.model.equipment[id].type === apsEnums.LineEquipmentType.Mcal
						|| vm.model.equipment[id].type === apsEnums.LineEquipmentType.Multi
						|| vm.model.equipment[id].type === apsEnums.LineEquipmentType.FlexBC
						|| vm.model.equipment[id].type === apsEnums.LineEquipmentType.FlexM
					) {
						vm.hasCavities = true;
						break;
					}
				}
			}));

	vm.wareLossClicked = (): void => {
		let stateParams = {
			plantId: vm.$stateParams.plantId,
			shopId: vm.$stateParams.shopId,
			userId: vm.$stateParams.userId,
		};
		$state.go(States.PLANT_WARE_LOSS_DASHBOARD, stateParams);
	}

	vm.toggleHelpWindow = (): void => {
		vm.showHelpWindow = !vm.showHelpWindow;
	}

	vm.showHelp = (helpScreen: string): void => {
		vm.helpText = getCountsHelpText(helpScreen);
		vm.toggleHelpWindow();
	}


	vm.toggleLineActive = (line: LineItem): void => {
		if (!vm.canDisableLine) {
			return;
		}

		let message = `Line ${line.name} is currently ${line.isDisabled ? 'disabled' : 'active'}. `
			+ `Would you like to ${line.isDisabled ? 'activate' : 'disable'} it?`;
		let subject = `${line.isDisabled ? 'Activate' : 'Disable'} Line ${line.name}?`;

		uiUtilityService.confirm(message, subject, 'Yes', 'No').then(confirmed => {
			if (!confirmed) {
				return;
			}

			let promise = lineConfigsService.toggleLine({
				id: line.id,
				shopId: line.shopId,
				isDisabled: !line.isDisabled
			}).then(() => line.isDisabled = !line.isDisabled);
			loaderService.loadingPromise(promise);
		});
	};

	vm.viewRejectDetail = (line: LineItem, equipment: LineEquipmentItem): void => {
		let modalPromise: angular.IPromise<any> = countsPromise;
		if (equipment.hasTabs || (equipment.detailDataPoints && equipment.detailDataPoints.length > 0)) {
			modalPromise = countsService.getEquipmentCounts(shopId, vm.model, equipment, vm.countsInterval)
				.then(counts => {
					vm.model.currentDetailCounts = counts;
				});
			loaderService.loadingPromise(modalPromise);
		}
		modalPromise.then(() => {
			var result = openEquipmentRejects({
				equipment: equipment,
				line: line,
				plant: vm.plant,
				shopId: shopId,
				canDisable: vm.canDisableLine,
				detail: vm.model.currentDetailCounts,
				lineConfig: vm.model
			}, $modal);

			result.then(() => {
				if (vm.model.currentDetailCounts) {
					delete vm.model.currentDetailCounts;
				}
			});
		});
	};

	vm.viewGroupRejectDetail = (equipmentGroup: LineEquipmentGroup): void => {
		vm.model.currentGroup = equipmentGroup;
		groupRejectsService.showGroupRejects({
			equipmentGroup: equipmentGroup,
			shopId: vm.shop.id,
			lineCounts: vm.model,
			countsInterval: vm.countsInterval
		});
	};

	vm.countsIntervalChanged = () => {
		vm.model.handleIntervalChange(vm.countsInterval);
		authDataService.setCountInterval(vm.countsInterval);
	};

	let promise = countsPromise.then(() => {
		vm.hub = countsService.createHub(shopId, vm.model);

		$scope.$on('$destroy', () => {
			if (vm.hub) {
				vm.hub.leaveGroup(shopId)
					.then(() => vm.hub.uninitialize());
			}
		});
	});

	loaderService.loadingPromise(promise);

	return vm;
}

function mapLineToRow(line: LineItem): CountRow {
	return {
		isLine: true,
		line: line,
		junction: null,
		cssClass: null
	};
}

function mapJunctionToRow(junction: LineJunctionItem): CountRow {
	return {
		isLine: false,
		line: null,
		junction: junction,
		cssClass: 'table' + (junction.inputs.length > junction.outputs.length
			? junction.inputs.length : junction.outputs.length).toString()
	};
}

function mapModelToAreas(model: LineCounts): CountArea[] {
	let areas = [];
	let area: CountArea = {
		rows: _.sortBy(model.lines.filter(l => l.startJunctionId === null), r => r.number)
			.map(l => mapLineToRow(l))
	};

	while (area.rows.length > 0) {
		areas.push(area);

		let previousLines = area.rows.filter(l => l.isLine).map(l => l.line);
		let rows = [];
		area.rows.forEach(row => {
			// if row is junction, add all of the output lines
			if (!row.isLine) {
				_.sortBy(row.junction.outputs, l => l.number).forEach(line => {
					rows.push(mapLineToRow(line));
				});
				return;
			}

			let junction = row.line.endJunction;
			if (!junction) {
				return;
			}

			// do not add the junction if it is already in the list of rows
			if (_.some(rows, r => !r.isLine && r.junction === junction)) {
				return;
			}

			// if the end junction references lines not in the previous area, add the line instead
			if (!junction.inputs.every(l => _.some(previousLines, al => al.id === l.id))) {
				rows.push(mapLineToRow(row.line));
			}

			rows.push(mapJunctionToRow(junction));
		});

		area = { rows: rows };
	}
	return areas;
}

function getCountsHelpText(helpScreen: string): string {
	let helptext = '';

	switch (helpScreen) {
		case 'EQUIPMENT COUNTS':
			helptext = `  
			<div class="header">
			  Equipment Counts
			</div>         
			<div class="subheader">
			  <u>How the Counts Work</u>:
			</div>
			<div class="detail">
				Below is a sample count object, this is sample data just for instructional purposes.
			</div>
			<div class="detail">
				<table>
					<tbody>
						<tr>
							<td>
								<div class="bs-card">
									<div class="equipment-name">SQR</div>
									<div class="count">11328</div>
									<div>
										<span class="fail-rate">166</span>
										<span class="fail-percentage">1.5%</span>
									</div>
									<br/>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div class="detail">
				As you can see the count object is broken up into 4 different rows. Here is the definition of each row:
			</div>  
			<div class="detail">
				<ol type = "1">
					<li class="detail">
						<b>Equipment Shorthand:</b> This is a shorthand abreviation of the type of equipment configured on the line.
						In this case this is a high speed squeezer. This is abreviated as SQR.
					</li>
					<li class="detail">
						<b>Inspect Count:</b> The number of bottles going into the machine.
						In APS the preference is to use a direct integration to the machine for this counter.
						However, there are instances that we are dependent on a photoeye before the inspection equipment 
						to provide the inspect count.
					</li>
					<li class="detail">
						<b>Reject Count:</b>
						The amount of bottles rejected by the piece of inspection equipment.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						For most equipment we have a direct integration and the equipment keeps counts of rejects.
						However, for some equipment integrations we need to calculated it by taking the difference between
						bottles in and bottles out.
						<br/>
						<span class="formula">Reject Count = Reject Count from Machine</span><br/>
						<span class="footnote">[where machine has direct integration]</span><br/>
						<span class="formula">Reject Count = Machine Bottles In - Machine Bottles Out</span><br/>
						<span class="footnote">[where machine only has input/output]</span><br/>
					</li>
					<li class="detail">
						<b>Reject Percent:</b>
						The percent of loss for each piece of equipment.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						This calculation is based off the equipment inspects vs. the rejects.
						<br/>
						<span class="formula">Reject Percent = Reject Count / Inspect Count</span><br/>
					</li>
				</ol>
			</div>
			<div class="subheader">
				<u>Equipment Details</u>:
			</div>   
			<div class="detail">
				Every piece of equipment has the ability to drill-down. 
				All the user has to do to access this data is to click on the peice of equipment.
				Depending on the type of integration some equipment will display more data than others.
			</div>
		  	`;
			break;
		case 'AREA EFFICIENCY':
			helptext = `  
			<div class="header">
			  Area Efficiency
			</div>         
			<div class="subheader">
			  <u>Metrics per Area</u>:
			</div>
			<div class="detail">
				Each area of the production line will have the following metrics, where applicable.
				The metric definitions are as follows.
			</div>
			<div class="detail">
				<ol type = "1">
					<li class="detail">
						<b>AREA IN:</b> 
						The total number of bottles going into each area.
						This number is used as the denominator for efficiency calculations in each area.
					</li>
					<li class="detail">
						<b>AREA REJECTS:</b> 
						The total amount of bottles rejected in each area.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
					</li>
					<li class="detail">
						<b>AREA REJECT:</b>
						The percent of loss for each area.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						<br/>
						<span class="formula">AREA REJECT = 100 * (Area Rejects / Area In)</span>
					</li>
					<li class="detail">
						<b>AREA EFFICIENCY:</b>
						The ammount of bottles to make it through each area compared to how many went in.
						This is the inverse of the <i>AREA REJECT %</i>.
						<br/>
						<span class="formula">AREA REJECT = 100 - (100 * (Area Rejects / Area In))</span>
					</li>
				</ol>
			</div>
		  	`;
			break;
		case 'SHOP KPI':
			helptext = `  
			<div class="header">
			  Shop KPI's
			</div>         
			<div class="subheader">
			  <u>KPI Metric Definitions</u>:
			</div>
			<div class="detail">
				The goal of these KPI's is to see how the shop is preforming at each point along the line.
				We use each pulse from the shear cuts to calculate how many gobs are currently being produced.
			</div>
			<div class="detail">
				<ol type = "1">
					<li class="detail">
						<b>MACHINE SPEED:</b>
						How many bottles per minute is the machine producing.
						Where available we use a direct machine integration.
						Otherwise, we use the shear pulses to estimate the speed.
					</li>
					<li class="detail">
						<b>LEHR IN:</b> 
						The percent of bottles going into the lehr, compared to the shears.
						<br/>
						<span class="formula">LEHR IN = Lehr In Counter / Shears</span>
					</li>
					<li class="detail">
						<b>LEHR OUT:</b>
						The percent of bottles comming out of the lehr, into the cold-end, compared to the shears.
						Usually we are using the sum of the first peice of inspection equipment.
						<br/>
						<span class="formula">LEHR OUT = Cold-End In / Shears</span>
					</li>
					<li class="detail">
						<b>PACK:</b>
						The percent of bottles that make it to the End of Line (EOL), compared to the shears.
						This is usually a photoeye, sometimes it can be the last piece of inspection equipment.
						It depends on the shop configuration.
						<br/>
						<span class="formula">PACK = EOL / Shears</span>
					</li>
				</ol>
			</div>
		  	`;
			break;
		case 'WARE LOSS':
			helptext = `  
			<div class="header">
				Shop Ware Loss
			</div>         
			<div class="subheader">
				<u>Shop Ware Loss Definitions</u>:
			</div>
			<div class="detail">
				The goal of these Metrics are quickly identify the areas of most loss compared to bottles created.
				We use each pulse from the shear cuts to calculate how many gobs are produced at the hot-end.
				Your total loss should add up to all the other ware loss percentages.
			</div>
			<div class="detail">
				<ol type = "1">
					<li class="detail">
						<b>HOT END LOSS:</b>
						The percent of the bottles produced that did not make it into the lehr.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						<br/>
						<span class="formula">HOT END LOSS = 100 - (100 * (LEHR IN / SHEARS))</span>
					</li>
					<li class="detail">
						<b>LEHR LOSS:</b> 
						The percent of bottles that were lost in the lehr, over the shears.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						<br/>
						<span class="formula">LEHR LOSS = 100 - (100 * ((LEHR IN - LEHR OUT) / SHEARS))</span>
					</li>
					<li class="detail">
						<b>AIE LOSS:</b>
						The percent of bottles lost to all inspection equipment, over the shears.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						<br/>
						<span class="formula">AIE LOSS = 100 - (100 * (AIE LOSS / SHEARS))</span>
					</li>
					<li class="detail">
						<b>CE DELTA LOSS:</b>
						The percent of all bottles that made it into the cold end, 
						that were not rejected by inspection equipment,
						but did not make it to the end of line,
						over the shears.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						<br/>
						<span class="formula">CE DELTA LOSS = 100 - (100 * (LEHR OUT - AIE LOSS - END OF LINE) / SHEARS)</span>
					</li>
					<li class="detail">
						<b>TOTAL LOSS:</b>
						The percent of bottles that did not make it to the End of Line (EOL), compared to the shears.
						This is usually a photoeye, sometimes it can be the last piece of inspection equipment.
						It depends on the shop configuration.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						<br/>
						<span class="formula">TOTAL LOSS = 100 - (100 * (END OF LINE / SHEARS))</span>
					</li>
				</ol>
			</div>
		  	`;
			break;
		default:
			helptext = 'Selected Help Screen Not Found...';
			break;
	}

	return helptext;
}



// WEBPACK FOOTER //
// ./src/app/controllers/counts/countv2.controller.ts